import { API_ROUTES } from "../api/ApiRoutes";
import { AppStrings } from "./constants/AppStrings";
import { formatString } from "./constants/stringUtils";
import { getPrivateInstance } from "../queries/_privateInstance";

/**
 * Icon upload for Feature Quick Access / profile shortcuts.
 *
 * Single-step JSON flow (same shape as Casino FM / Sportsbook icon updates):
 *   `POST /admin-panel-v2/configurations/profile-shortcuts/icon`
 *   with `{ shortcut_id, image: "data:<mime>;base64,..." }` → CDN URL for
 *   the shortcut's `icon` field before the subsequent full-list PUT.
 *
 * Client checks (type, 512 KB) exist for fast feedback; the server
 * re-validates (JPEG/JPG/PNG/SVG, SVG sanitization).
 */

/** MIME types the profile-shortcut icon endpoint accepts. */
export const PROFILE_SHORTCUT_ICON_CONTENT_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/svg+xml",
] as const;

type ProfileShortcutIconContentType =
  (typeof PROFILE_SHORTCUT_ICON_CONTENT_TYPES)[number];

/** 512 KB cap, mirrored from the backend profile-shortcuts icon route. */
export const MAX_PROFILE_SHORTCUT_ICON_BYTES = 512 * 1024;

/** Subset of the upload response the flow depends on. */
interface ProfileShortcutIconUploadResponse {
  /** CloudFront URL returned by `POST .../profile-shortcuts/icon`. */
  url?: string;
  /** Alternate field names some gateways use. */
  cloudfront_url?: string;
  image_url?: string;
  public_url?: string;
  icon_url?: string;
  icon?: string;
}

type UploadEnvelope = {
  data?: ProfileShortcutIconUploadResponse;
} & ProfileShortcutIconUploadResponse;

/**
 * Normalize / accept MIME types the endpoint allows. `image/jpg` is treated
 * as valid JPEG. Browsers sometimes leave MIME blank on drop — fall back to
 * extension.
 */
const resolveContentType = (
  file: File,
): Exclude<ProfileShortcutIconContentType, "image/jpg"> | null => {
  if (file.type === "image/jpeg" || file.type === "image/jpg") return "image/jpeg";
  if (file.type === "image/png") return "image/png";
  if (file.type === "image/svg+xml") return "image/svg+xml";
  const name = (file.name || "").toLowerCase();
  if (name.endsWith(".jpeg") || name.endsWith(".jpg")) return "image/jpeg";
  if (name.endsWith(".png")) return "image/png";
  if (name.endsWith(".svg")) return "image/svg+xml";
  return null;
};

/** Read the file as a data-URL, forcing the resolved MIME into the prefix. */
const fileToDataUrl = (file: File, contentType: string): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== "string" || !result.includes(",")) {
        reject(new Error(AppStrings.PAM_SHORTCUT_ICON_READ_FAILED));
        return;
      }
      const base64 = result.slice(result.indexOf(",") + 1);
      resolve(`data:${contentType};base64,${base64}`);
    };
    reader.onerror = () =>
      reject(new Error(AppStrings.PAM_SHORTCUT_ICON_READ_FAILED));
    reader.readAsDataURL(file);
  });

/**
 * Map transport failures to operator-friendly messages instead of surfacing
 * axios's "Request failed with status code NNN".
 */
const toFriendlyUploadError = (error: unknown): Error => {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  if (status === 401) {
    return new Error(AppStrings.PAM_SHORTCUT_ICON_SESSION_EXPIRED);
  }
  if (status === 403) {
    return new Error(AppStrings.PAM_SHORTCUT_ICON_UPLOAD_PERMISSION_DENIED);
  }
  const message = (error as { response?: { data?: { message?: string } } })
    ?.response?.data?.message;
  if (typeof message === "string" && message.trim()) {
    return new Error(message);
  }
  return new Error(AppStrings.PAM_SHORTCUT_ICON_UPLOAD_FAILED);
};

const resolvePublicUrl = (
  uploaded: ProfileShortcutIconUploadResponse,
): string | undefined =>
  uploaded.url ||
  uploaded.cloudfront_url ||
  uploaded.image_url ||
  uploaded.public_url ||
  uploaded.icon_url ||
  uploaded.icon;

export type UploadProfileShortcutIconParams = {
  file: File;
  /** Shortcut id — sent as `shortcut_id` so the backend can key the object. */
  shortcutId: string;
};

/**
 * Upload a profile-shortcut icon as a JSON data-URL. Resolves with the CDN
 * URL to store on the shortcut before Save Changes issues the full-list PUT.
 */
export const uploadProfileShortcutIcon = async ({
  file,
  shortcutId,
}: UploadProfileShortcutIconParams): Promise<string> => {
  const contentType = resolveContentType(file);
  if (!contentType) {
    throw new Error(AppStrings.PAM_SHORTCUT_ICON_INVALID_TYPE);
  }
  if (file.size > MAX_PROFILE_SHORTCUT_ICON_BYTES) {
    throw new Error(
      formatString(AppStrings.PAM_SHORTCUT_ICON_TOO_LARGE, {
        maxKb: String(Math.floor(MAX_PROFILE_SHORTCUT_ICON_BYTES / 1024)),
      }),
    );
  }

  let image: string;
  try {
    image = await fileToDataUrl(file, contentType);
  } catch (error) {
    throw error instanceof Error
      ? error
      : new Error(AppStrings.PAM_SHORTCUT_ICON_READ_FAILED);
  }

  let uploaded: ProfileShortcutIconUploadResponse;
  try {
    const uploadResponse = await getPrivateInstance().post<UploadEnvelope>(
      API_ROUTES.PROFILE_SHORTCUT_ICON_UPLOAD,
      {
        shortcut_id: shortcutId,
        image,
      },
    );
    uploaded = uploadResponse?.data?.data ?? uploadResponse?.data ?? {};
  } catch (error) {
    throw toFriendlyUploadError(error);
  }

  const publicUrl = resolvePublicUrl(uploaded);
  if (!publicUrl) {
    throw new Error(AppStrings.PAM_SHORTCUT_ICON_MISSING_URL);
  }

  return publicUrl;
};
